import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTopOnRouteChange component
 * - Automatically scrolls to top when navigating between routes
 * - Handles anchor/hash links with smooth scroll
 * - Throttled to prevent excessive scroll events
 * - Respects scroll-margin-top for fixed headers
 */
export const ScrollToTopOnRouteChange = () => {
  const { pathname, hash } = useLocation();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    // Only act if pathname actually changed (not just hash)
    const pathnameChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;

    if (hash) {
      // Handle anchor link - delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          // Uses scroll-margin-top from CSS for fixed header offset
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    } else if (pathnameChanged) {
      // Instant scroll to top for route changes (no smooth to prevent jank)
      // RAF ensures it happens after render
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant' as ScrollBehavior,
        });
      });
    }
  }, [pathname, hash]);

  return null;
};
